import React from 'react'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import Skeleton from '@material-ui/lab/Skeleton'
import cx from 'classnames'
import { range } from 'libraries/lodash'

export const StoriesSkeleton = () => (
  <Grid container spacing={2} className='bg-ghost-grey' style={{ marginTop: 0 }}>
    <Hidden xsDown>
      <Grid item xs={12} style={{ paddingTop: 0 }}>
        <HeadlineStorySkeleton />
      </Grid>
    </Hidden>
    <Hidden smUp>
      <Grid item className='w-100'>
        <StorySkeleton />
      </Grid>
    </Hidden>
    {range(0, 8).map((i) => (
      <Grid key={i} item xs={12} sm={6} md={4} lg={4}>
        <StorySkeleton />
      </Grid>
    ))}
    <Hidden only='sm'>
      <Grid item xs={12} sm={6} md={4} lg={4}>
        <StorySkeleton />
      </Grid>
    </Hidden>
  </Grid>
)

const HeadlineSkeletonText = () => (<Skeleton animation='wave' classes={{ root: 'h3' }} />)

const StorySkeleton = () => (
  <div className='ca-grid-card pa6 bg-white'>
    <Grid container className='h-100'>
      <Grid item xs={12} className='w-100'>
        <TileSkeleton />
      </Grid>
      <Grid item xs={12} className='w-100' height={154}>
        <Skeleton
          variant='rect'
          animation='pulse'
          classes={{ root: 'bg-void-grey w-100 mv6' }}
          height={154}
        />
      </Grid>
      <Grid item xs={12} className='w-100'>
        <Skeleton classes={{ root: 'w-65' }} />
      </Grid>
    </Grid>
  </div>
)

const TileSkeleton = () => (
  <Grid container direction='column' justify='space-between' className={cx('relative h-100')}>
    <Grid item className='max-w-100'>
      <Skeleton classes={{ root: 'w-65' }} />
      <ContentSkeleton />
    </Grid>
  </Grid>
)

const ContentSkeleton = () => (
  <div className='ca-content black ca-homepage'>
    <div className='b ca-tile-title'>
      {range(0, 6).map(i => <Skeleton variant='text' animation='wave' key={i} />)}
    </div>
  </div>
)

const HeadlineStorySkeleton = () => (
  <Grid container className='ca-grid-card pb0 pt6 pl6 pr0 bg-white'>
    <Grid item sm={6} md={4} className='h-100'>
      <Skeleton variant='text' classes={{ root: 'w-60' }} />
      {range(0, 7).map((i) => <HeadlineSkeletonText key={i} />)}
      <Skeleton variant='text' classes={{ root: 'w-60' }} />
    </Grid>
    <Grid item sm={6} md={8} className='h-100 pl6 pb8'>
      <Skeleton
        variant='rect'
        animation='pulse'
        classes={{ root: 'h-100 bg-void-grey' }}
      />
    </Grid>
  </Grid>
)

export const AnnouncementsSkeleton = ({ maxHeight }) => (
  <div className='bg-twilight-grey relative'>
    <div style={{ maxHeight }}>
      <div className='announcements-content pa6 f4' style={{ overflow: 'hidden' }}>
        {range(0, 20).map(i => <Skeleton key={i} variant='text' animation='wave' />)}
      </div>
    </div>
  </div>
)

export const CommentsSkeleton = () => (
  <>
    <AnnotationControlsSkeleton />
    <CommentSkeleton />
    <CommentSkeleton />
  </>
)

const CommentSkeleton = () => (
  <div className='ph6 mt6 mb6'>
    <AnnotationQuestionSkeleton />
    <div className='min-h12'>
      <CommentHeaderSkeleton />
      <CommentContentSkeleton />
    </div>
  </div>
)

const CommentContentSkeleton = () => (
  <section className='ph6 pt4 pb6'>
    <Skeleton
      variant='text'
      animation='wave'
      classes={{ root: 'f4 fw6 w15 mb6' }}
    />
    <Skeleton
      variant='text'
      animation='wave'
      classes={{ root: 'f5 w14' }}
    />
    <Skeleton
      variant='text'
      animation='wave'
      classes={{ root: 'f5 w14' }}
    />
  </section>
)

const CommentHeaderSkeleton = () => (
  <div className='flex justify-between items-center bg-ice-blue'>
    <Grid container alignItems='center' spacing={1} className='ph6 pv4'>
      <PrivateCommentLabelSkeleton />
      <CommentPartyDetailsSkeleton />
    </Grid>
    <SpeedDialControlSkeleton />
  </div>
)

const CommentPartyDetailsSkeleton = () => (
  <>
    <Grid item>
      <Skeleton
        variant='text'
        animation='wave'
        classes={{ root: 'f5 w11' }}
      />
    </Grid>
    <Grid item>
      <Skeleton
        variant='text'
        animation='wave'
        classes={{ root: 'f5 w11' }}
      />
    </Grid>
  </>
)

const PrivateCommentLabelSkeleton = () => (
  <Grid item>
    <Skeleton
      variant='rect'
      animation='wave'
      height={20}
      classes={{ root: 'w11' }}
    />
  </Grid>
)

const SpeedDialControlSkeleton = () => (
  <div className='pv2'>
    <Skeleton
      variant='circle'
      animation='pulse'
      height={40}
      width={40}
      classes={{ root: 'mr6' }}
    />
  </div>
)

const AnnotationQuestionSkeleton = () => (
  <div className='mb6 mt4'>
    <div className='flex items-center justify-between'>
      <Skeleton
        variant='text'
        animation='wave'
        classes={{ root: 'f5 w-60' }}
      />
      <div className='flex'>
        <Skeleton
          variant='circle'
          animation='pulse'
          height={32}
          width={32}
          classes={{ root: '' }}
        />
        <Skeleton
          variant='circle'
          animation='pulse'
          height={32}
          width={32}
          classes={{ root: 'ml4 mr5' }}
        />
      </div>
    </div>
    <CommentBreadcrumbsSkeleton />
  </div>
)

const AnnotationControlsSkeleton = () => (
  <div className='bg-ice-blue flex justify-between ph6 pv3'>
    <Skeleton
      variant='text'
      animation='wave'
      classes={{ root: 'f3 w13' }}
    />
    <Skeleton
      variant='text'
      animation='wave'
      classes={{ root: 'f3 w12' }}
    />
  </div>
)

const CommentBreadcrumbsSkeleton = () => (
  <div className='flex items-center flex-wrap w-100 mt4'>
    {range(1, 5).map(index => (
      <div key={index}>
        <Skeleton
          variant='text'
          animation='wave'
          classes={{ root: 'f6 mr6 w12' }}
        />
      </div>
    ))}
  </div>
)
